import { Icon, IconButton, Typography } from '@material-ui/core'
import { DatePicker, KeyboardDatePicker, KeyboardTimePicker } from '@material-ui/pickers'
import { MaterialUiPickersDate } from '@material-ui/pickers/typings/date'
import React from 'react'

interface Props {
    openingDate?: Date
    closingDate?: Date
    selectedMonth: number
    selectedYear: number
    onMonthChange: (selected: number) => Promise<void>
    disabled: boolean
    onOpeningChange: (date: Date) => void
    onClosingChange: (date: Date) => void
}

type ChangeType = 'opening' | 'closing'

const fieldsStyle: React.CSSProperties = { width: 220 }
const pickerContainerStyle: React.CSSProperties = { display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minWidth: 320 }
const mainContainerStyle: React.CSSProperties = { display: 'grid', gridTemplateColumns: 'repeat(2, minmax(320px, 1fr))', alignItems: 'center', justifyItems: 'center', flexGrow: 1, height: '100%', gap: 24, padding: '16px 24px' }

const EmptyToolbar = () => null

const isValidDate = (date: MaterialUiPickersDate): date is Date => {
    return Boolean(date && !isNaN(date.getTime()))
}

const normalizeDate = (date: Date): Date => {
    const normalizedDate = new Date(date.getTime())
    normalizedDate.setSeconds(0, 0)
    return normalizedDate
}

const DisabledDay = ({ day, dayInCurrentMonth }: { day: MaterialUiPickersDate, dayInCurrentMonth: boolean }) => {
    return (
        <IconButton className={`MuiButtonBase-root MuiIconButton-root MuiPickersDay-day ${!dayInCurrentMonth ? 'MuiPickersDay-hidden' : ''}`} disableRipple disabled style={{ cursor: 'default', width: 36, height: 36 }}>
            <span className="MuiIconButton-label">
                <Typography color="textSecondary" variant="body2">
                    {day?.getDate()}
                </Typography>
            </span>
        </IconButton>
    )
}

const StartEndStaticPicker = (props: Props) => {
    const { openingDate, closingDate, selectedMonth, selectedYear, onMonthChange, disabled, onOpeningChange, onClosingChange } = props

    const [internalOpeningDate, setInternalOpeningDate] = React.useState<Date | null>(openingDate ? new Date(openingDate) : null)
    const [internalClosingDate, setInternalClosingDate] = React.useState<Date | null>(closingDate ? new Date(closingDate) : null)

    React.useEffect(() => {
        setInternalOpeningDate(openingDate ? new Date(openingDate) : null)
    }, [openingDate])

    React.useEffect(() => {
        setInternalClosingDate(closingDate ? new Date(closingDate) : null)
    }, [closingDate])

    const notifyChange = (date: Date, type: ChangeType) => {
        const normalizedDate = normalizeDate(date)

        if (type === 'opening') {
            onOpeningChange(normalizedDate)
        }

        if (type === 'closing') {
            onClosingChange(normalizedDate)
        }
    }

    const handleMonthChange = async (date: MaterialUiPickersDate) => {
        if (isValidDate(date)) {
            await onMonthChange(date.getMonth())
        }
    }

    const handleDateChange = (date: MaterialUiPickersDate, type: ChangeType) => {
        if (!isValidDate(date)) return

        const currentDate = type === 'opening' ? internalOpeningDate : internalClosingDate
        const updatedDate = new Date(date.getTime())

        if (currentDate && !isNaN(currentDate.getTime())) {
            updatedDate.setHours(currentDate.getHours(), currentDate.getMinutes(), 0, 0)
        } else {
            updatedDate.setSeconds(0, 0)
        }

        if (type === 'opening') {
            setInternalOpeningDate(updatedDate)
        } else {
            setInternalClosingDate(updatedDate)
        }

        notifyChange(updatedDate, type)
    }

    const handleTimeChange = (time: MaterialUiPickersDate, type: ChangeType) => {
        if (!isValidDate(time)) return

        const currentDate = type === 'opening' ? internalOpeningDate : internalClosingDate
        const updatedDate = currentDate && !isNaN(currentDate.getTime())
            ? new Date(currentDate.getTime())
            : new Date(selectedYear, selectedMonth - 1, 1, 0, 0, 0, 0)

        updatedDate.setHours(time.getHours(), time.getMinutes(), 0, 0)

        if (type === 'opening') {
            setInternalOpeningDate(updatedDate)
        } else {
            setInternalClosingDate(updatedDate)
        }

        notifyChange(updatedDate, type)
    }

    const renderDisabledDay = (day: MaterialUiPickersDate, _: MaterialUiPickersDate, dayInCurrentMonth: boolean) => {
        return <DisabledDay day={day} dayInCurrentMonth={dayInCurrentMonth} />
    }

    return (
        <div style={mainContainerStyle}>
            <div style={pickerContainerStyle}>
                <div className="flex flex-col justify-center" style={{ ...fieldsStyle, paddingBottom: 12 }}>
                    <Typography className="opacity-75 font-bold uppercase" variant="body1">
                        Fecha de apertura
                    </Typography>

                    <div className="flex items-center">
                        <Icon className="my-2 mr-2" color="action">event_available</Icon>

                        <KeyboardDatePicker value={internalOpeningDate} onChange={date => handleDateChange(date, 'opening')} disabled={disabled} format="dd-MM-yyyy" inputVariant="outlined" size="small" fullWidth placeholder="dd-mm-yyyy" KeyboardButtonProps={{ style: { display: 'none' } }} FormHelperTextProps={{ style: { display: 'none' } }} />
                    </div>

                    <div className="flex items-center">
                        <Icon className="my-2 mr-2" color="action">access_time</Icon>

                        <KeyboardTimePicker value={internalOpeningDate} onChange={time => handleTimeChange(time, 'opening')} disabled={disabled || !internalOpeningDate} ampm={false} format="HH:mm" inputVariant="outlined" size="small" fullWidth placeholder="hh:mm" KeyboardButtonProps={{ style: { display: 'none' } }} FormHelperTextProps={{ style: { display: 'none' } }} />
                    </div>
                </div>

                <DatePicker autoOk variant="static" openTo="date" disabled={disabled} value={internalOpeningDate} initialFocusedDate={new Date(selectedYear, selectedMonth - 1, 1)} onChange={date => handleDateChange(date, 'opening')} onMonthChange={handleMonthChange} renderDay={disabled ? renderDisabledDay : undefined} ToolbarComponent={EmptyToolbar} />
            </div>

            <div style={pickerContainerStyle}>
                <div className="h-full py-4 px-6 flex flex-col" style={fieldsStyle}>
                    <Typography className="opacity-75 font-bold uppercase" variant="body1">
                        Fecha de cierre
                    </Typography>

                    <div className="flex items-center">
                        <Icon className="my-2 mr-2" color="action">event_busy</Icon>

                        <KeyboardDatePicker value={internalClosingDate} onChange={date => handleDateChange(date, 'closing')} disabled={disabled} format="dd-MM-yyyy" inputVariant="outlined" size="small" fullWidth placeholder="dd-mm-yyyy" KeyboardButtonProps={{ style: { display: 'none' } }} FormHelperTextProps={{ style: { display: 'none' } }} />
                    </div>

                    <div className="flex items-center">
                        <Icon className="my-2 mr-2" color="action">access_time</Icon>

                        <KeyboardTimePicker value={internalClosingDate} onChange={time => handleTimeChange(time, 'closing')} disabled={disabled || !internalClosingDate} ampm={false} format="HH:mm" inputVariant="outlined" size="small" fullWidth placeholder="hh:mm" KeyboardButtonProps={{ style: { display: 'none' } }} FormHelperTextProps={{ style: { display: 'none' } }} />
                    </div>
                </div>

                <DatePicker autoOk variant="static" openTo="date" disabled={disabled} value={internalClosingDate} initialFocusedDate={new Date(selectedYear, selectedMonth - 1, 1)} onChange={date => handleDateChange(date, 'closing')} onMonthChange={handleMonthChange} renderDay={disabled ? renderDisabledDay : undefined} ToolbarComponent={EmptyToolbar} />
            </div>
        </div>
    )
}

export default StartEndStaticPicker